/**
 * RTM listener for outbound:delivered events.
 * Shows an info toast when the outbound message is delivered to the user's device.
 * Only fires for the agent who initiated the message.
 */
import { useCallback, useContext, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { SocketContext } from '@/contexts/rtm';
import { selectUserId } from '@/redux/user/selectors';
import { OutboundDeliveredPayload, ServerToClientEvents, SocketEvent } from '@/shared/types/rtm';

export const useRtmOutboundDelivered = () => {
  const { socket } = useContext(SocketContext);
  const userId = useSelector(selectUserId);
  const { t } = useTranslation();

  const handleOutboundDelivered: ServerToClientEvents[SocketEvent.outbound_delivered] = useCallback(
    ({ data }: { data: OutboundDeliveredPayload }) => {
      if (data.initiator_user_id !== userId) {
        return;
      }

      toast.info(t('conversation.outbound_delivered'));
    },
    [t, userId]
  );

  useEffect(() => {
    socket?.on(SocketEvent.outbound_delivered, handleOutboundDelivered);
    return () => {
      socket?.off(SocketEvent.outbound_delivered, handleOutboundDelivered);
    };
  }, [handleOutboundDelivered, socket]);
};
